import { useState } from "react";
import { Box, Paper, Typography, useMediaQuery } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import {
  BooleanField,
  Button,
  Datagrid,
  DateField,
  FunctionField,
  List,
  ReferenceField,
  ReferenceInput,
  SelectField,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  useGetList,
  useNotify,
  useRecordContext,
  useStore,
} from "react-admin";
import { useNavigate } from "react-router-dom";
import { settings } from "app";
import permissions from "app/permissions";
import { getPermissions } from "auth/security/securityUtils";
import CustomFilter from "features/CustomFilter";
import { MobileListRow } from "features/MobileListRow";
import { EmptyList, EmptyWithAction } from "server/components";
import { MoneyField } from "server/components/Fields/MoneyField";
import { checkPermission } from "server/security/utils";
import { administrationRequests } from "api/administrationApi";
import { dormitoryRequests } from "api/dormitoryApi";
import { DormitoryStudentTypeChoices } from "utils/choices";
import {
  DormitoryServicePath,
  MOBILE_BREAKPOINT,
  ROOM_TYPES,
  SUBSIDIES,
} from "utils/constants";
import DormitoryAutocompleteInput from "../Partials/DormitoryAutocompleteInput";
import DatagridActions from "../Partials/DatagridActions";
import { normalizeSearch } from "../Partials/searchNormalize";
import CustomDateInput from "server/components/Fields/CustomDateInput";

const ROOM_TYPE_STORE_KEY = "dormitoryStateSubsidies.filter";

const listFilters = [
  <ReferenceInput
    key="receptionTypeId"
    source="receptionTypeId"
    reference={administrationRequests.getReceptionTypesSelect()}
    alwaysOn
  >
    <DormitoryAutocompleteInput
      label="Вид прием"
      optionText="name"
      emptyText="Всички"
      style={{ width: 280 }}
    />
  </ReferenceInput>,
  <DormitoryAutocompleteInput
    key="studentType"
    source="studentType"
    label="Тип студент"
    choices={DormitoryStudentTypeChoices}
    alwaysOn
    emptyText="Всички"
    style={{ width: 280 }}
  />,
  <CustomDateInput
    key="validOn"
    source="validOn"
    label="Валидна на"
    alwaysOn
    noTime
    style={{ width: 280 }}
  />,
];

const CreateSubsidyButton = ({ typeId, roomTypeName }) => {
  const navigate = useNavigate();
  if (!checkPermission(getPermissions(), permissions.canManageDormitoryStateSubsidies)) {
    return null;
  }
  return (
    <Button
      startIcon={<AddIcon />}
      label="Създай"
      onClick={() =>
        navigate(`/${DormitoryServicePath}${ROOM_TYPES}/${typeId}/${SUBSIDIES}/create`, {
          state: { roomTypeName },
        })
      }
    />
  );
};

const EditSubsidyButton = ({ typeId, roomTypeName }) => {
  const navigate = useNavigate();
  const record = useRecordContext();
  if (!record || !checkPermission(getPermissions(), permissions.canManageDormitoryStateSubsidies)) {
    return null;
  }
  return (
    <Button
      startIcon={<EditIcon />}
      label="Редактирай"
      onClick={() =>
        navigate(`/${DormitoryServicePath}${ROOM_TYPES}/${typeId}/${SUBSIDIES}/${record.id}/edit`, {
          state: { roomTypeName },
        })
      }
    />
  );
};

const ListStateSubsidies = () => {
  const notify = useNotify();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const [filters, setFilters] = useStore(ROOM_TYPE_STORE_KEY, null);
  const [typeId, setTypeId] = useState(filters?.typeId ?? null);

  const { data: roomTypes = [] } = useGetList(dormitoryRequests.getRoomTypesSelect(), {});
  const roomTypeChoices = roomTypes.map(rt => ({ id: rt.id, name: rt.name || rt.nameBg }));
  const committedTypeId = filters?.typeId;
  const roomTypeName = roomTypeChoices.find(rt => rt.id === +committedTypeId)?.name;
  const subsidiesResource = `${DormitoryServicePath}${ROOM_TYPES}/${committedTypeId}/${SUBSIDIES}`;

  const handleSearch = () => {
    if (!typeId) {
      notify("Моля, изберете тип стая!", { type: "error" });
      return;
    }
    setFilters({ typeId });
  };

  return (
    <>
      {!filters && (
        <Title
          title="Държавни субсидии"
        />
      )}
      <CustomFilter
        title="Държавни субсидии"
        cardWidth={isMobile ? "100%" : "700px"}
        buttonLabel="Провери"
        buttonVariant="text"
        buttonOnClick={handleSearch}
        filters={[
          {
            label: "Тип стая",
            value: typeId,
            setValue: setTypeId,
            type: "autocomplete",
            multiple: false,
            choices: roomTypeChoices,
            filterOptions: (options, { inputValue }) => {
              if (!inputValue.trim()) {
                return options;
              }
              const query = normalizeSearch(inputValue);
              return options.filter(option => normalizeSearch(option.nameBg || option.name).includes(query));
            },
          },
        ]}
      />
      {!filters && (
        <Paper>
          <Box
            mt={3}
          >
            <Typography
              variant="inherit"
              align="center"
              color="secondary"
            >
              Въведете тип стая за преглед на държавните субсидии.
            </Typography>
          </Box>
        </Paper>
      )}
      {filters && committedTypeId && (
        <List
          resource={subsidiesResource}
          title={roomTypeName ? `Държавни субсидии: ${roomTypeName}` : "Държавни субсидии"}
          filters={listFilters}
          exporter={false}
          empty={
            <EmptyWithAction
              message="Няма намерени резултати."
            >
              <CreateSubsidyButton
                typeId={committedTypeId}
                roomTypeName={roomTypeName}
              />
            </EmptyWithAction>
          }
          actions={
            <TopToolbar>
              <CreateSubsidyButton
                typeId={committedTypeId}
                roomTypeName={roomTypeName}
              />
            </TopToolbar>
          }
        >
          {isMobile ? (
            <SimpleList
              empty={<EmptyList />}
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={() => (
                <MobileListRow
                  fields={[
                    {
                      label: "Субсидия",
                      value: (
                        <MoneyField
                          source="amount"
                        />
                      ),
                    },
                  ]}
                />
              )}
              secondaryText={() => (
                <div>
                  <MobileListRow
                    fields={[
                      {
                        label: "Вид прием",
                        value: (
                          <ReferenceField
                            source="receptionTypeId"
                            reference={administrationRequests.getReceptionTypesSelect()}
                            link={false}
                          >
                            <TextField
                              source="name"
                            />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Тип студент",
                        value: (
                          <SelectField
                            source="studentType"
                            choices={DormitoryStudentTypeChoices}
                          />
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Валидна от",
                        value: (
                          <DateField
                            source="validFrom"
                            locales="bg-BG"
                          />
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Валидна до",
                        value: (
                          <DateField
                            source="validTo"
                            locales="bg-BG"
                          />
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Ретроактивна",
                        value: (
                          <BooleanField
                            source="isRetroactive"
                          />
                        ),
                      },
                    ]}
                  />
                </div>
              )}
              tertiaryText={() => (
                <EditSubsidyButton
                  typeId={committedTypeId}
                  roomTypeName={roomTypeName}
                  isMobile
                />
              )}
            />
          ) : (
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
              empty={<EmptyList />}
            >
              <MoneyField
                source="amount"
                label="Субсидия"
                textAlign="left"
              />
              <ReferenceField
                source="receptionTypeId"
                reference={administrationRequests.getReceptionTypesSelect()}
                label="Вид прием"
                link={false}
              >
                <TextField
                  source="name"
                />
              </ReferenceField>
              <SelectField
                source="studentType"
                label="Тип студент"
                choices={DormitoryStudentTypeChoices}
              />
              <DateField
                source="validFrom"
                label="Валидна от"
                locales="bg-BG"
              />
              <DateField
                source="validTo"
                label="Валидна до"
                locales="bg-BG"
              />
              <BooleanField
                source="isRetroactive"
                label="Ретроактивна"
              />
              <FunctionField
                source="editAction"
                label={false}
                sortable={false}
                render={() => (
                  <DatagridActions>
                    <EditSubsidyButton
                      typeId={committedTypeId}
                      roomTypeName={roomTypeName}
                      isMobile={false}
                    />
                  </DatagridActions>
                )}
              />
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};

export default ListStateSubsidies;
